import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/api";
import { FileText, Mail, ShieldCheck, CheckCircle2, ExternalLink, Eye, Eraser, PenLine } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// 🆕 Feb 2026 — Worker PDF.js via CDN pinnato alla versione installata (react-pdf 10.4.1 → pdfjs-dist 5.4.x)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

/**
 * 🆕 Feb 2026 — Pagina pubblica firma OTP (redesign).
 * URL: /sign/:sigId
 *
 * Layout:
 *  - Header con logo agenzia
 *  - Card con info documento + preview PDF (iframe, ~70vh)
 *  - Pulsante "Ricevi codice OTP via email" abilitato SOLO dopo aver visualizzato il documento
 *  - Sezione OTP appare dopo l'invio
 */
export default function SignDocument() {
  const { sigId } = useParams();
  const [loading, setLoading] = useState(true);
  const [sigInfo, setSigInfo] = useState(null);
  const [error, setError] = useState("");
  const [channel] = useState("email");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("view"); // view | enter | done
  const [busy, setBusy] = useState(false);
  const [signedInfo, setSignedInfo] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [pdfViewed, setPdfViewed] = useState(false);
  const [consented, setConsented] = useState(false);
  const [drawnDataUrl, setDrawnDataUrl] = useState("");
  const [hasStroke, setHasStroke] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(760);
  const otpSectionRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef({ down: false, last: null });
  const pdfContainerRef = useRef(null);

  // 🆕 Feb 2026 — Misura il container per rendere le pagine PDF responsive (mobile/desktop)
  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth || el.getBoundingClientRect().width || 760;
      setContainerWidth(Math.max(300, Math.min(w - 24, 980)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sigInfo?.has_pdf]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/signatures/${sigId}`);
        setSigInfo(data);
        if (data.status === "signed") {
          setStep("done");
          setSignedInfo({ signed_at: data.signed_at });
        } else if (!data.has_pdf) {
          // Se non c'è un PDF da mostrare (quote/policy/quote_offer), abilito subito il bottone
          setPdfViewed(true);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Richiesta firma non trovata o scaduta");
      } finally {
        setLoading(false);
      }
    })();
  }, [sigId]);

  // Countdown OTP
  useEffect(() => {
    if (step !== "enter" || expiresIn <= 0) return;
    const t = setInterval(() => setExpiresIn((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [step, expiresIn]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/public/signatures/${sigId}/send-otp`, { channel });
      if (data.ok) {
        toast.success(`Codice inviato via ${channel}`);
        setExpiresIn(data.expires_in_seconds || 600);
        setResendCooldown(30);
        setStep("enter");
        setTimeout(() => otpSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
      } else {
        toast.error(data.delivery_note || "Impossibile inviare il codice");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio codice");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) { toast.error("Inserisci un codice di 6 cifre"); return; }
    if (sigInfo?.require_drawn_signature && !hasStroke) {
      toast.error("Disegna la tua firma nel riquadro prima di procedere");
      return;
    }
    setBusy(true);
    try {
      const payload = { otp };
      if (sigInfo?.require_drawn_signature && drawnDataUrl) {
        payload.drawn_signature = drawnDataUrl;
      }
      const { data } = await axios.post(`${API_URL}/public/signatures/${sigId}/verify-otp`, payload);
      toast.success("Firma confermata!");
      setSignedInfo(data);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Codice errato o scaduto");
    } finally {
      setBusy(false);
    }
  };

  // 🆕 Feb 2026 — Utility canvas per firma grafica manuale
  const getCanvasPoint = (ev) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
    const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const beginDraw = (ev) => {
    ev.preventDefault();
    const p = getCanvasPoint(ev);
    if (!p) return;
    drawingRef.current.down = true;
    drawingRef.current.last = p;
  };

  const moveDraw = (ev) => {
    if (!drawingRef.current.down) return;
    ev.preventDefault();
    const p = getCanvasPoint(ev);
    const ctx = canvasRef.current?.getContext("2d");
    if (!p || !ctx) return;
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.last.x, drawingRef.current.last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    drawingRef.current.last = p;
    if (!hasStroke) setHasStroke(true);
  };

  const endDraw = () => {
    if (!drawingRef.current.down) return;
    drawingRef.current.down = false;
    const canvas = canvasRef.current;
    if (canvas) setDrawnDataUrl(canvas.toDataURL("image/png"));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawnDataUrl("");
    setHasStroke(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-500">Caricamento…</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white border border-rose-200 rounded-xl p-6 max-w-md text-center shadow-sm">
        <div className="text-4xl mb-2">⚠️</div>
        <h1 className="font-semibold text-slate-900 mb-2">Errore</h1>
        <p className="text-sm text-slate-600">{error}</p>
      </div>
    </div>
  );

  const mins = Math.floor(expiresIn / 60);
  const secs = expiresIn % 60;
  const logoUrl = sigInfo?.agency_id ? `${API_URL}/public/agency-logo/${sigInfo.agency_id}` : null;
  const pdfSrc = sigInfo?.has_pdf ? `${API_URL}/public/signatures/${sigId}/document.pdf#toolbar=1&view=FitH` : null;
  const docTitle = sigInfo?.doc_label || sigInfo?.document_title || sigInfo?.summary?.description || "Documento";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-page">
      {/* Header con logo agenzia */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo agenzia"
                className="h-10 w-auto max-w-[160px] object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="sign-agency-logo"
              />
            )}
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Firma elettronica</div>
              <div className="font-semibold text-slate-900 truncate">{docTitle}</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            OTP · Sicura
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Card info documento */}
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6" data-testid="sign-doc-info">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900">{docTitle}</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Gentile <span className="font-medium text-slate-700">{sigInfo?.client_name}</span>, controlla il documento e procedi con la firma.
              </p>
              {sigInfo?.summary?.amount != null && (
                <p className="text-sm mt-2">
                  <span className="text-slate-500">Importo:</span>{" "}
                  <span className="font-mono font-medium text-slate-900">{formatCurrency(sigInfo.summary.amount)}</span>
                </p>
              )}
            </div>
          </div>
        </section>

        {/* PDF Viewer — react-pdf renderizza in canvas su ogni dispositivo (desktop+mobile) */}
        {sigInfo?.has_pdf && step !== "done" && (
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="sign-pdf-section">
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                <Eye className="h-4 w-4 text-slate-500" />
                Anteprima documento
                {numPages > 0 && (
                  <span className="text-xs text-slate-500 font-normal">· {numPages} {numPages === 1 ? "pagina" : "pagine"}</span>
                )}
                {pdfViewed && (
                  <span className="ml-1 inline-flex items-center gap-1 text-emerald-700 text-xs bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                    <CheckCircle2 className="h-3 w-3" /> Visualizzato
                  </span>
                )}
              </div>
              {/* Link di emergenza sempre presente per sicurezza */}
              <a
                href={pdfSrc}
                target="_blank"
                rel="noreferrer"
                onClick={() => setPdfViewed(true)}
                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 hover:underline"
                data-testid="sign-pdf-open-external"
              >
                Apri in una nuova finestra <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            {pdfError ? (
              /* Fallback: react-pdf non ha potuto caricare il documento */
              <div className="p-6 sm:p-8 flex flex-col items-center text-center gap-3" data-testid="sign-pdf-fallback">
                <div className="h-16 w-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center">
                  <FileText className="h-8 w-8 text-amber-600" />
                </div>
                <p className="text-sm text-slate-600 max-w-md">
                  Non è stato possibile visualizzare il PDF direttamente qui. Apri il documento in una nuova
                  finestra per leggerlo prima di firmare.
                </p>
                <a
                  href={pdfSrc}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setPdfViewed(true)}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl shadow-sm"
                  data-testid="sign-pdf-open-mobile"
                >
                  <Eye className="h-5 w-5" /> Apri il documento
                </a>
              </div>
            ) : (
              <div
                ref={pdfContainerRef}
                className="bg-slate-100 overflow-y-auto overflow-x-hidden"
                style={{ maxHeight: "min(75vh, 900px)" }}
                data-testid="sign-pdf-iframe"
              >
                <Document
                  file={pdfSrc}
                  options={PDF_OPTIONS}
                  onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPdfViewed(true); setPdfError(false); }}
                  onLoadError={(err) => { console.warn("react-pdf load error:", err); setPdfError(true); }}
                  loading={
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="sign-pdf-loading">
                      <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin mb-2" />
                      <div>Caricamento documento…</div>
                    </div>
                  }
                  className="flex flex-col items-center py-3"
                >
                  {Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={`page_${i + 1}`}
                      pageNumber={i + 1}
                      width={containerWidth}
                      renderTextLayer
                      renderAnnotationLayer
                      className="mb-3 shadow-sm border border-slate-200 bg-white"
                    />
                  ))}
                </Document>
              </div>
            )}
          </section>
        )}

        {/* Sezione firma */}
        {step === "view" && (
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6" data-testid="sign-otp-cta">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-slate-900">Pronto a firmare?</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Riceverai un codice OTP a 6 cifre via email a{" "}
                  <span className="font-mono text-slate-700">{sigInfo?.client_email_masked}</span>.
                </p>
              </div>
            </div>

            {!pdfViewed && sigInfo?.has_pdf && (
              <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="sign-otp-locked-hint">
                📖 Leggi il documento qui sopra per abilitare l&apos;invio del codice OTP.
              </div>
            )}

            {/* 🆕 Feb 2026 — Consenso esplicito obbligatorio prima di richiedere l'OTP */}
            <label className="flex items-start gap-2 mb-4 cursor-pointer select-none" data-testid="sign-consent-wrapper">
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-blue-600"
                data-testid="sign-consent-checkbox"
              />
              <span className="text-sm text-slate-700">
                <strong>Ho letto e compreso il documento</strong> e ne accetto il contenuto.
              </span>
            </label>

            <button
              type="button"
              onClick={sendOtp}
              disabled={busy || !consented || (!pdfViewed && !!sigInfo?.has_pdf)}
              data-testid="send-otp-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-xl transition shadow-sm text-base"
            >
              {busy ? "Invio in corso…" : "Ricevi codice OTP via email"}
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-3">
              Firma elettronica con OTP · ID richiesta: <span className="font-mono">{sigId.slice(0, 8)}</span>
            </p>
          </section>
        )}

        {step === "enter" && (
          <section ref={otpSectionRef} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6" data-testid="sign-otp-section">
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-slate-900">Inserisci il codice OTP</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Ti abbiamo inviato un codice a 6 cifre via {channel}. Controlla la tua casella (anche spam).
                </p>
              </div>
            </div>

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              data-testid="otp-input"
              className="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 border-2 border-slate-300 focus:border-blue-500 rounded-xl mb-3 outline-none"
              placeholder="••••••"
            />
            <div className="text-center text-xs text-slate-500 mb-4">
              {expiresIn > 0 ? `Codice valido per ${mins}:${String(secs).padStart(2, "0")}` : "⚠️ Codice scaduto — richiedine uno nuovo"}
            </div>

            {/* 🆕 Feb 2026 — Firma grafica manuale (canvas) se richiesta dall'operatore */}
            {sigInfo?.require_drawn_signature && (
              <div className="mb-4" data-testid="sign-drawn-section">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-800 inline-flex items-center gap-1.5">
                    <PenLine className="h-4 w-4 text-indigo-600" /> Firma qui con dito o mouse
                  </label>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
                    data-testid="sign-drawn-clear"
                  >
                    <Eraser className="h-3.5 w-3.5" /> Pulisci
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  width={640}
                  height={220}
                  onMouseDown={beginDraw}
                  onMouseMove={moveDraw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={beginDraw}
                  onTouchMove={moveDraw}
                  onTouchEnd={endDraw}
                  className="w-full bg-white border-2 border-dashed border-slate-300 rounded-lg touch-none"
                  style={{ height: "180px", cursor: "crosshair" }}
                  data-testid="sign-drawn-canvas"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  {hasStroke ? "✓ Firma acquisita — puoi ripulire e rifare se necessario" : "Traccia la tua firma nel riquadro qui sopra."}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy || otp.length !== 6 || (sigInfo?.require_drawn_signature && !hasStroke)}
              data-testid="verify-otp-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold py-3.5 rounded-xl transition mb-2"
            >
              {busy ? "Verifica…" : "✓ Firma documento"}
            </button>
            <button
              type="button"
              onClick={() => { setOtp(""); sendOtp(); }}
              disabled={busy || resendCooldown > 0}
              data-testid="resend-otp-btn"
              className="w-full border border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 text-sm font-medium py-2.5 rounded-xl transition"
            >
              {resendCooldown > 0
                ? `🔄 Invia di nuovo (${resendCooldown}s)`
                : "🔄 Non hai ricevuto l'email? Invia di nuovo"}
            </button>
          </section>
        )}

        {step === "done" && (
          <section className="bg-white border border-emerald-200 rounded-2xl shadow-sm p-8 text-center" data-testid="sign-done-section">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-emerald-700 mb-2">Documento firmato!</h2>
            <p className="text-sm text-slate-600 mb-2">
              La tua firma è stata registrata
              {signedInfo?.signed_at ? ` il ${new Date(signedInfo.signed_at).toLocaleString("it-IT")}` : ""}.
            </p>
            <p className="text-xs text-slate-400">Puoi chiudere questa pagina in tutta sicurezza.</p>
          </section>
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-center text-[11px] text-slate-400">
        Firma elettronica con OTP · Validità codice 10 minuti
      </footer>
    </div>
  );
}
